import { useEffect, useRef, useState } from 'react';

interface UseFetchTimeoutResult {
  /** True once a single request has been loading longer than the window. */
  timedOut: boolean;
  /** Clears the timeout and starts the window again, for a Retry control. */
  restart: () => void;
}

/**
 * Fails a request that never settles, so the page can offer Retry instead of
 * sitting on a skeleton.
 *
 * `scopeKey` identifies what the request is for. Re-scoping abandons the
 * request in flight without `isLoading` ever dropping, so the window has to
 * start again — otherwise the replacement inherits what was left of the old
 * one and can time out immediately.
 */
export function useFetchTimeout(
  isLoading: boolean,
  timeoutMs: number,
  scopeKey: string,
): UseFetchTimeoutResult {
  const [timedOut, setTimedOut] = useState(false);
  const [nonce, setNonce] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (isLoading) {
      // A new scope starts the window again, and clears a timeout the previous
      // scope reached, so re-scoping is a way out of the error state.
      setTimedOut(false);
      timerRef.current = setTimeout(() => {
        setTimedOut(true);
      }, timeoutMs);
    } else {
      setTimedOut(false);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isLoading, timeoutMs, scopeKey, nonce]);

  const restart = () => {
    setTimedOut(false);
    // Re-arms even when the request was already in flight and hung.
    setNonce((current) => current + 1);
  };

  return { timedOut, restart };
}
